import React, { useEffect, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { IoCheckmark } from "react-icons/io5";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { updateMessageOptions } from "../../../store/Slices/MessageOptionsSlice";
import { updateSpesificUserData } from "../../../store/Slices/UserSlice";
import useApiPost from "../../../hooks/PostData";
import toast from "react-hot-toast";
import TextTranslate from "../../../utils/TextTranslate";
import { useTranslateText } from "../../../hooks/useTranslateText";
import { ClipLoader } from "react-spinners";

const groupClass =
  "overflow-hidden rounded-2xl bg-secondary shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06]";
const divide =
  "[&>*+*]:border-t [&>*+*]:border-black/[0.06] dark:[&>*+*]:border-white/[0.08]";

const aboutOption = [
  "At Work",
  "Available",
  "At Office",
  "Battery about to die",
  "In a meeting",
  "At the gym",
  "Sleeping",
];

const SelectAboutModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const translate = useTranslateText();
  const userDetails = useAppSelector((state) => state.userData);
  const MessageOptions = useAppSelector((state) => state.MessageOptions);
  const { loading, postData } = useApiPost();
  const [about, setAbout] = useState(userDetails.bio);

  useEffect(() => {
    setAbout(userDetails.bio);
  }, [userDetails.bio, MessageOptions.show_select_about_modal]);

  const isPreset = aboutOption.includes(about || "");
  const inputValue = isPreset ? translate(about) : about || "";

  const handleCloseModal = () => {
    dispatch(
      updateMessageOptions({
        show_select_about_modal: false,
      }),
    );
  };

  async function updateUserAbout() {
    await postData("users/updateUser", { bio: about });
    toast.success("Profile Updated", { position: "bottom-left" });
    dispatch(updateSpesificUserData({ bio: about }));
    handleCloseModal();
  }

  return (
    <Dialog
      open={MessageOptions.show_select_about_modal}
      onClose={handleCloseModal}
      className="relative z-[100]"
    >
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm">
        <div className="flex min-h-full items-end justify-center p-0 sm:items-center sm:p-4">
          <DialogPanel className="relative flex max-h-[85dvh] w-full max-w-md flex-col overflow-hidden rounded-t-[1.25rem] bg-primary shadow-2xl sm:rounded-2xl">
            <DialogTitle className="shrink-0 border-b border-black/[0.06] px-4 py-3.5 text-center text-[17px] font-semibold tracking-tight text-darkText dark:border-white/[0.08]">
              <TextTranslate text="About" />
            </DialogTitle>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
              <div className="mb-1.5 px-1 text-[13px] font-medium uppercase tracking-wide text-lightText">
                <TextTranslate text="Currently set to" />
              </div>
              <div className={`${groupClass} mb-5 px-4 py-3`}>
                <input
                  value={inputValue}
                  onChange={(e) => setAbout(e.target.value)}
                  className="w-full bg-transparent text-[17px] text-darkText outline-none placeholder:text-lightText"
                  placeholder={translate("Select your about")}
                />
              </div>

              <div className="mb-1.5 px-1 text-[13px] font-medium uppercase tracking-wide text-lightText">
                <TextTranslate text="Select your about" />
              </div>
              <div className={`${groupClass} ${divide}`}>
                {aboutOption.map((option) => {
                  const selected = option == about;
                  return (
                    <button
                      type="button"
                      key={option}
                      onClick={() => setAbout(option)}
                      className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition active:bg-black/[0.03] dark:active:bg-white/[0.04]"
                    >
                      <span className="min-w-0 flex-1 text-[17px] text-darkText">
                        <TextTranslate text={option} />
                      </span>
                      {selected ? (
                        <IoCheckmark className="shrink-0 text-xl text-themeColor" />
                      ) : (
                        <span className="h-5 w-5 shrink-0" aria-hidden />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="shrink-0 border-t border-black/[0.06] bg-primary/95 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur-xl dark:border-white/[0.08]">
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="rounded-xl bg-black/[0.06] py-3 text-[17px] font-medium text-darkText transition active:scale-[0.99] dark:bg-white/10"
                >
                  <TextTranslate text="Cancel" />
                </button>
                <button
                  type="button"
                  onClick={() => void updateUserAbout()}
                  className="rounded-xl bg-themeColor py-3 text-[17px] font-semibold text-themeTextColor transition active:scale-[0.99]"
                >
                  {loading ? (
                    <ClipLoader size={18} color="currentColor" />
                  ) : (
                    <TextTranslate text="Save" />
                  )}
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default SelectAboutModal;
